import React, { useEffect, useState } from "react";
import { Plus, ArrowUpRight, Milestone } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, EmptyState } from "../components/ui";

export default function Lifecycle() {
  const { user, meta } = useAuth();
  const [items, setItems] = useState(null);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { employeeId: "", type: "PROMOTION", effectiveDate: "", newDesignationId: "", newDepartmentId: "", newLocationId: "", newManagerId: "", hikePercent: "", reason: "" };
  const [form, setForm] = useState(blank);
  const isHR = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => api.get("/lifecycle/transitions").then(({ data }) => setItems(data));
  useEffect(() => { load(); api.get("/employees?limit=200").then(({ data }) => setEmps(data.items)).catch(() => {}); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, hikePercent: form.hikePercent ? +form.hikePercent : 0 };
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      await api.post("/lifecycle/transitions", body);
      alert("Transition applied"); setOpen(false); setForm(blank); load();
    } catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  if (!items) return <Loading />;

  return (
    <div>
      <PageHeader title="Promotions & Transfers" subtitle="Employee lifecycle transitions with instant org & salary updates">
        {isHR && <button data-testid="new-transition-btn" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> New Transition</button>}
      </PageHeader>
      {items.length === 0 ? <EmptyState icon={Milestone} title="No promotions or transfers yet" /> : (
        <div className="space-y-4">
          {items.map((t) => (
            <Card key={t.id} className="p-6" data-testid={`transition-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${t.type === "PROMOTION" ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15" : "bg-sky-100 text-sky-600 dark:bg-sky-500/15"}`}><ArrowUpRight size={16} /></span>
                    <div>
                      <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">{t.employeeName}</h3>
                      <p className="text-xs text-slate-400">Effective {fmtDate(t.effectiveDate)} · by {t.createdBy}</p>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-1 text-sm text-slate-600 dark:text-slate-300">
                    {t.from?.designation !== t.to?.designation && <p>Designation: <b>{t.from?.designation || "—"}</b> → <b className="text-accent">{t.to?.designation || "—"}</b></p>}
                    {t.from?.department !== t.to?.department && <p>Department: <b>{t.from?.department || "—"}</b> → <b className="text-accent">{t.to?.department || "—"}</b></p>}
                    {t.from?.location !== t.to?.location && <p>Location: <b>{t.from?.location || "—"}</b> → <b className="text-accent">{t.to?.location || "—"}</b></p>}
                    {t.hikePercent > 0 && <p>Salary revision: <b className="text-emerald-600">+{t.hikePercent}%</b></p>}
                    {t.reason && <p className="text-xs text-slate-400">Reason: {t.reason}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2"><Badge value={t.type} tone={t.type === "PROMOTION" ? "ok" : "info"} /><Badge value={t.status} /></div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title="New Promotion / Transfer"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button form="tr-form" className="btn-accent">Apply</button></>}>
        <form id="tr-form" onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Employee *</label>
              <select required data-testid="tr-emp" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} className="input"><option value="">— Select —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
            <div><label className="label">Type</label>
              <select data-testid="tr-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input">{["PROMOTION", "TRANSFER"].map((t) => <option key={t}>{t}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Effective Date</label><input type="date" data-testid="tr-date" value={form.effectiveDate} onChange={(e) => setForm({ ...form, effectiveDate: e.target.value })} className="input" /></div>
            <div><label className="label">Hike %</label><input type="number" step="0.5" data-testid="tr-hike" value={form.hikePercent} onChange={(e) => setForm({ ...form, hikePercent: e.target.value })} className="input" placeholder="0" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">New Designation</label>
              <select data-testid="tr-desig" value={form.newDesignationId} onChange={(e) => setForm({ ...form, newDesignationId: e.target.value })} className="input"><option value="">— No change —</option>{meta?.designations?.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}</select></div>
            <div><label className="label">New Department</label>
              <select data-testid="tr-dept" value={form.newDepartmentId} onChange={(e) => setForm({ ...form, newDepartmentId: e.target.value })} className="input"><option value="">— No change —</option>{meta?.departments?.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">New Location</label>
              <select value={form.newLocationId} onChange={(e) => setForm({ ...form, newLocationId: e.target.value })} className="input"><option value="">— No change —</option>{meta?.locations?.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
            <div><label className="label">New Manager</label>
              <select value={form.newManagerId} onChange={(e) => setForm({ ...form, newManagerId: e.target.value })} className="input"><option value="">— No change —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}</select></div>
          </div>
          <div><label className="label">Reason</label><textarea data-testid="tr-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input h-16 py-2" /></div>
        </form>
      </Modal>
    </div>
  );
}
